import { useState } from "react";
/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useSnackbar } from "notistack";
import { useAsyncData } from "./useAsyncData";
import * as trashApi from "../../../api/trash";
import { ConfirmDeleteDialog } from "./components/DriveDialogs";
import { formatBytes, formatDate } from "./format";

// Module scope keeps the reference stable for useAsyncData.
const fetchTrash = () => trashApi.listTrash().then((files) => ({ files }));

const TrashPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [confirming, setConfirming] = useState(null); // {kind, item}
  const state = useAsyncData(fetchTrash, { files: [] }, "Could not load the trash.");


  const restore = async (file) => {
    try {
      await trashApi.restore(file.id);
      enqueueSnackbar(`“${file.file_name}” restored.`, { variant: "success" });
      state.reload();
    } catch (err) {
      enqueueSnackbar(err.message || "Restore failed.", { variant: "error" });
    }
  };

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "accent.main" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>Trash</Typography>
          <Typography sx={{ fontSize: 12.5, color: "secondaryText.color" }}>
            Deleted files stay here until you remove them permanently.
          </Typography>
        </Box>
        {state.files.length > 0 && (
          <Button
            startIcon={<DeleteForeverOutlinedIcon />}
            sx={{ color: "error.main", textTransform: "none" }}
            onClick={() => setConfirming({ kind: "empty" })}
          >
            Empty trash
          </Button>
        )}
      </Box>

      {state.error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={state.reload}>Retry</Button>}>
          {state.error}
        </Alert>
      )}

      <Paper variant="outlined">
        {state.files.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: "center", fontSize: 13.5, color: "secondaryText.color" }}>
            Trash is empty.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell width={120}>Size</TableCell>
                <TableCell width={140}>Deleted</TableCell>
                <TableCell width={110} />
              </TableRow>
            </TableHead>
            <TableBody>
              {state.files.map((file) => (
                <TableRow key={file.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <InsertDriveFileIcon fontSize="small" sx={{ color: "text.color", opacity: 0.5 }} />
                      <Typography variant="body2" noWrap>{file.file_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatBytes(file.size_bytes)}</TableCell>
                  <TableCell>{formatDate(file.updated_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Restore">
                      <IconButton size="small" onClick={() => restore(file)}>
                        <RestoreFromTrashOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete permanently">
                      <IconButton
                        size="small"
                        sx={{ color: "error.main" }}
                        onClick={() => setConfirming({ kind: "one", item: file })}
                      >
                        <DeleteForeverOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <ConfirmDeleteDialog
        open={confirming?.kind === "one"}
        items={confirming?.item ? [{ id: confirming.item.id, name: confirming.item.file_name, type: "file" }] : []}
        onConfirm={async () => {
          await trashApi.deleteForever(confirming.item.id);
          state.reload();
        }}
        onClose={() => setConfirming(null)}
      />
      <ConfirmDeleteDialog
        open={confirming?.kind === "empty"}
        items={state.files.map((f) => ({ id: f.id, name: f.file_name, type: "file" }))}
        onConfirm={async () => {
          const n = await trashApi.emptyTrash();
          enqueueSnackbar(`${n} item(s) permanently deleted.`, { variant: "success" });
          state.reload();
        }}
        onClose={() => setConfirming(null)}
      />
    </Box>
  );
};

export default TrashPage;
